/**
 * ParsedRow[] -> Transaction[].
 *
 * This is where issuer-shaped rows become domain objects: merchant
 * normalization, occurrence counting, dedupe keys and ids. Pure, so the whole
 * import decision can be tested without a database.
 */

import {
  assignOccurrences,
  buildDedupeKey,
  buildManualDedupeKey,
  transactionIdFromKey,
  type DedupeInput,
} from './identity.ts';
import { normalizeMerchant } from './text.ts';
import { MANUAL_SOURCE_FILE_ID, type InstallmentInfo, type Transaction } from './types.ts';
import type { ParsedRow } from '../parsers/types.ts';

export interface BuildContext {
  personId: string;
  cardId: string;
  sourceFileId: string;
  /** Statement-level charge date; Isracard states it once per file. */
  chargeDate: string | null;
  importedAt: string;
}

export function buildTransactions(
  rows: readonly ParsedRow[],
  context: BuildContext,
): Transaction[] {
  const bases = rows.map((row) => ({
    personId: context.personId,
    cardId: context.cardId,
    transactionDate: row.transactionDate,
    amountILS: row.amountILS,
    merchantNormalized: normalizeMerchant(row.merchantRaw),
    installment: row.installment,
    reference: row.reference,
  }));

  const occurrences = assignOccurrences(bases);

  return rows.map((row, index) => {
    const base = bases[index] as Omit<DedupeInput, 'occurrence'>;
    const input: DedupeInput = { ...base, occurrence: occurrences[index] ?? 0 };
    const dedupeKey = buildDedupeKey(input);

    return {
      id: transactionIdFromKey(dedupeKey),
      dedupeKey,
      personId: context.personId,
      cardId: context.cardId,
      transactionDate: row.transactionDate,
      chargeDate: context.chargeDate,
      merchantRaw: row.merchantRaw,
      merchantNormalized: base.merchantNormalized,
      amountILS: row.amountILS,
      originalAmount: row.originalAmount,
      originalCurrency: row.originalCurrency,
      categoryId: null,
      subcategoryId: null,
      notes: null,
      tags: [],
      installment: row.installment,
      isRefund: row.amountILS < 0,
      isStandingOrder: row.isStandingOrder,
      excludedFromReports: false,
      reference: row.reference,
      extraDetails: row.extraDetails,
      sourceFileId: context.sourceFileId,
      importedAt: context.importedAt,
      updatedAt: context.importedAt,
      manuallyEdited: false,
    } satisfies Transaction;
  });
}

/** What a person can type in. Everything else is derived. */
export interface ManualEntry {
  personId: string;
  cardId: string;
  transactionDate: string;
  /** Statement-level on an import; per-row here, and optional. */
  chargeDate: string | null;
  merchantRaw: string;
  /** Negative for a refund or credit. */
  amountILS: number;
  categoryId: string | null;
  subcategoryId: string | null;
  installment: InstallmentInfo | null;
  isStandingOrder: boolean;
  notes: string | null;
}

/**
 * A hand-entered row.
 *
 * `uid` and `now` are parameters rather than read from the environment, so this
 * stays pure and the tests can pin both — the same reason the import path takes
 * `importedAt`.
 *
 * `reference` is deliberately null. It is the issuer's voucher number, and this
 * row has no issuer; setting one would send `buildDedupeKey` down its `ref`
 * branch and hand the row an id derived from someone else's numbering.
 */
export function buildManualTransaction(
  entry: ManualEntry,
  options: { uid: string; now: string },
): Transaction {
  const dedupeKey = buildManualDedupeKey(options.uid);

  return {
    id: transactionIdFromKey(dedupeKey),
    dedupeKey,
    personId: entry.personId,
    cardId: entry.cardId,
    transactionDate: entry.transactionDate,
    chargeDate: entry.chargeDate,
    merchantRaw: entry.merchantRaw,
    merchantNormalized: normalizeMerchant(entry.merchantRaw),
    amountILS: entry.amountILS,
    originalAmount: null,
    originalCurrency: null,
    categoryId: entry.categoryId,
    subcategoryId: entry.subcategoryId,
    notes: entry.notes,
    tags: [],
    installment: entry.installment,
    isRefund: entry.amountILS < 0,
    isStandingOrder: entry.isStandingOrder,
    excludedFromReports: false,
    reference: null,
    extraDetails: null,
    sourceFileId: MANUAL_SOURCE_FILE_ID,
    importedAt: options.now,
    updatedAt: options.now,
    // Set from the start: this row exists only because a human made it, so a
    // destructive undo must never treat it as reproducible from a file.
    manuallyEdited: true,
  } satisfies Transaction;
}
